import axios from "axios";
import { getCachedResponse, invalidateBootstrapCache } from "./dashboardBootstrap";
import BACKEND_URL from "./apiConfig";
import { createBackendAvailability } from "./backendAvailability";

/**
 * Resilient network middleware.
 * - Retries GET requests on server/network errors with exponential backoff + jitter
 * - Honors server-provided Retry-After header (emitted by our 503 handlers)
 * - Mutations are NOT retried globally (components handle their own)
 * - Detects chunk load errors and triggers graceful reload
 * - **Circuit breaker** (iter 249): when backend is marked DOWN, new GETs
 *   fail fast and pending retries are aborted — prevents the retry-storm
 *   amplification that was hammering a struggling backend with 3x the load.
 * - **In-flight dedup** (iter 249): concurrent GETs to the same URL share
 *   one pending promise — kills SWR+axios duplicate bursts on dashboard load.
 */

const RETRY_CODES = new Set([408, 429, 500, 502, 503, 504]);
const NO_RETRY = new Set([400, 401, 403, 404, 422, 423]);
const MAX_RETRIES = 3;
const BASE_DELAY = 500; // ms — first wait is ~500ms + jitter, then 1s, then 2s

let chunkReloadAttempts = 0;

// Global backend-down tracking. Emits CustomEvents so any component can
// react (e.g., show a "reconnecting..." banner).
//   - Fires 'backend-down'  when we see 5 consecutive 5xx / network failures
//     AND the first failure in the streak was >= 2 seconds ago (wall clock).
//     The wall-clock requirement prevents a burst of parallel in-flight
//     requests from tripping the banner during a single transient DB blip.
//   - Fires 'backend-up'    when the next successful response arrives
// Events carry { reason, status, retryAfterMs } for diagnostics.
let firstFailureAt = 0;

function emitBackendEvent(name, detail = {}) {
  if (typeof window === "undefined") return;
  try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* noop */ }
}

const availability = createBackendAvailability({
  probe: async () => {
    const res = await axios.get(`${BACKEND_URL}/api/health/ready`, {
      timeout: 4000, _retryCount: 99, _skipDedup: true, _skipBootstrapCache: true, _availabilityProbe: true,
    });
    return res.status === 200 && res.data?.status === "ready";
  },
  onDown: () => {
    startIncident();
    emitBackendEvent("backend-down", { reason: "readiness_failed" });
  },
  onUp: () => {
    endIncident();
    emitBackendEvent("backend-up", {});
    window.__EMERGENT_QUERY_CLIENT?.invalidateQueries({ refetchType: "active" });
  },
});
function markFailure() { if (!firstFailureAt) firstFailureAt = Date.now(); availability.failure(); }
function markSuccess() { availability.success(); if (!availability.isDown()) firstFailureAt = 0; }

// ── Banner-trip incident telemetry (iter 371b) ─────────────────────
// Ring buffer of recent request failures. When the circuit breaker trips
// we snapshot it; when the backend recovers we post the incident report
// so production "Server unavailable" banners can be diagnosed after the
// fact (timeout vs 5xx vs network tells very different stories).
const FAILURE_LOG_MAX = 20;
const recentFailures = [];
let activeIncident = null;
const INCIDENT_LS_KEY = "banner_trip_incident";

function recordFailure(config, status, reason) {
  recentFailures.push({
    url: (config?.url || "").replace(/^https?:\/\/[^/]+/, ""),
    method: (config?.method || "get").toUpperCase(),
    status: status || null,
    reason,
    at: new Date().toISOString(),
  });
  if (recentFailures.length > FAILURE_LOG_MAX) recentFailures.shift();
}

function startIncident() {
  activeIncident = {
    started_at: new Date(firstFailureAt || Date.now()).toISOString(),
    page: typeof window !== "undefined" ? window.location.pathname : null,
    failures: [...recentFailures],
  };
  try { localStorage.setItem(INCIDENT_LS_KEY, JSON.stringify(activeIncident)); } catch { /* noop */ }
}

function endIncident() {
  if (!activeIncident) return;
  const report = {
    ...activeIncident,
    ended_at: new Date().toISOString(),
    duration_ms: Date.now() - Date.parse(activeIncident.started_at),
    resolved: true,
    user_name: localStorage.getItem("user_name") || null,
    device_info: typeof navigator !== "undefined" ? navigator.userAgent : null,
  };
  activeIncident = null;
  try { localStorage.removeItem(INCIDENT_LS_KEY); } catch { /* noop */ }
  sendBannerTripReport(report);
}

function sendBannerTripReport(report) {
  try {
    axios.post(`${BACKEND_URL}/api/error-logs/banner-trip`, report, { timeout: 8000, _skipDedup: true })
      .catch(() => { /* telemetry is best-effort */ });
  } catch { /* noop */ }
}

// Page reloaded mid-outage? Flush the stored incident as unresolved once
// the app has settled (auth token present, backend presumably back).
if (typeof window !== "undefined") {
  try {
    const stale = localStorage.getItem(INCIDENT_LS_KEY);
    if (stale) {
      localStorage.removeItem(INCIDENT_LS_KEY);
      const inc = JSON.parse(stale);
      setTimeout(() => sendBannerTripReport({
        ...inc,
        ended_at: null,
        duration_ms: null,
        resolved: false,
        user_name: localStorage.getItem("user_name") || null,
        device_info: navigator.userAgent,
      }), 5000);
    }
  } catch { /* noop */ }
}

export function isBackendDown() { return availability.isDown(); }
export function retryBackendConnection() { return availability.retry(); }

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Exponential backoff with full jitter:
 *   attempt=1 → ~500-750ms
 *   attempt=2 → ~1000-1500ms
 *   attempt=3 → ~2000-3000ms
 * Jitter prevents retry storms when many clients fail at the same instant.
 */
function backoffDelay(attempt) {
  const exp = BASE_DELAY * Math.pow(2, attempt - 1);
  const jitter = Math.random() * (exp * 0.5);
  return exp + jitter;
}

/**
 * Parse the HTTP Retry-After header — either delta-seconds (e.g. "2")
 * or HTTP-date. Returns milliseconds to wait, or null if not present/parseable.
 */
function parseRetryAfter(headerValue) {
  if (!headerValue) return null;
  const asNum = Number(headerValue);
  if (!Number.isNaN(asNum) && asNum >= 0) return asNum * 1000;
  const asDate = Date.parse(headerValue);
  if (!Number.isNaN(asDate)) {
    const delta = asDate - Date.now();
    return delta > 0 ? delta : null;
  }
  return null;
}

// ── In-flight request dedup (iter 249) ─────────────────────────────
// When multiple components + SWR fire the same GET concurrently (the
// dashboard does this on login — 20+ endpoints, many with duplicate
// calls across stores), we share one pending promise per URL so the
// backend gets one request instead of N.
const inflightGets = new Map();

function inflightKey(config) {
  // Include URL + serialized params for dedup safety
  const paramStr = config.params ? JSON.stringify(config.params) : "";
  return `${(config.method || "get").toLowerCase()}:${config.url}:${paramStr}`;
}

// ── Circuit breaker token (iter 249) ──────────────────────────────
// When the backend is marked DOWN, we pause:
//   * New non-health GETs — they reject immediately with a BackendDownError
//   * Pending retry timers — they abort rather than fire into the fire
// This is the single biggest lever for stopping the cascade: without it,
// 20 dashboard endpoints × 3 retries = 80 requests hammering a pod that
// is already unhealthy, ensuring it stays unhealthy.
const pendingRetryCancellers = new Set();

export class BackendDownError extends Error {
  constructor() {
    super("Backend unreachable — paused non-critical requests");
    this.name = "BackendDownError";
    this.isBackendDown = true;
  }
}

function isHealthProbe(config) {
  const url = config?.url || "";
  return url.includes("/api/health");
}

function isMutation(config) {
  const m = (config?.method || "get").toLowerCase();
  return m !== "get" && m !== "head" && m !== "options";
}

// Abort all pending retries when backend goes down.
if (typeof window !== "undefined") {
  window.addEventListener("backend-down", () => {
    for (const cancel of pendingRetryCancellers) {
      try { cancel(); } catch { /* noop */ }
    }
    pendingRetryCancellers.clear();
  });
}

// Response interceptor
axios.interceptors.response.use(
  (response) => {
    // Any REAL 2xx response means the backend is alive — clear down state.
    // Synthetic bootstrap-cache hits never touched the network, so they
    // must NOT flip the circuit breaker back up during an outage.
    if (response.headers?.["x-bootstrap-cache"] !== "hit" && !isHealthProbe(response.config) &&
        !response.config?._availabilityProbe && !response.config?.url?.includes("/api/error-logs/") &&
        !response.config?.url?.includes("/api/public/")) {
      markSuccess();
    }
    const key = inflightKey(response.config || {});
    inflightGets.delete(key);
    return response;
  },
  async (error) => {
    const config = error.config;
    if (!config) return Promise.reject(error);

    const key = inflightKey(config);
    inflightGets.delete(key);

    const method = (config.method || "get").toLowerCase();
    if (config._availabilityProbe || isHealthProbe(config)) return Promise.reject(error);
    const status = error.response?.status;
    const isNetworkErr = !error.response && (error.code === "ERR_NETWORK" || error.message === "Network Error" || error.code === "ECONNABORTED");

    // iter 370ky — Public endpoints (login-photo wall, render-invoice
    // marketing pages) are not part of the authenticated app's critical
    // path. A 503 on `/api/public/login-photo/abc` should NOT trip the
    // global "Server unavailable" banner — those endpoints are loaded
    // from `<img>` tags on the login screen, are independent of the
    // backend's health for actual app traffic, and have their own
    // graceful-fail behaviour (broken image tile). Treat them as if
    // they were a 4xx client error for circuit-breaker accounting.
    const url = config.url || "";
    const isPublicEndpoint = url.includes("/api/public/");

    // Track backend health: 5xx and network errors = backend trouble;
    // 4xx (except 408/429) are client errors and shouldn't flip the banner.
    if (isPublicEndpoint || config._availabilityRecorded || url.includes("/api/error-logs/")) {
      // Don't move the circuit breaker either way — public endpoints are
      // background image fetches, not authenticated app health.
    } else if (isNetworkErr) {
      config._availabilityRecorded = true;
      // "timeout" (client gave up waiting — server slow but maybe alive)
      // vs "network" (connection refused/reset — server actually down).
      const reason = error.code === "ECONNABORTED" ? "timeout" : "network";
      recordFailure(config, 0, reason);
      markFailure(0, reason);
    } else if (status && (status >= 500 || status === 408 || status === 429)) {
      config._availabilityRecorded = true;
      recordFailure(config, status, `http_${status}`);
      markFailure(status, `http_${status}`);
    } else if (status && status < 500) {
      // A clean 4xx means the backend responded — it's alive.
      markSuccess();
    }

    // Chunk load errors
    if (error.message?.includes("Loading chunk") || error.message?.includes("Loading CSS chunk") || error.name === "ChunkLoadError") {
      if (chunkReloadAttempts < 2) { chunkReloadAttempts++; window.location.reload(); }
      return Promise.reject(error);
    }

    // Circuit breaker: if backend is down, don't retry non-health GETs —
    // just fail fast. This stops the 3x retry amplification from slamming
    // a pod that's already struggling.
    if (isBackendDown() && !isHealthProbe(config)) {
      return Promise.reject(error);
    }

    // Only retry GET requests
    if (method === "get") {
      if (status && NO_RETRY.has(status)) return Promise.reject(error);
      const shouldRetry = isNetworkErr || (status && RETRY_CODES.has(status));
      if (shouldRetry) {
        config._retryCount = config._retryCount || 0;
        if (config._retryCount < MAX_RETRIES) {
          config._retryCount++;

          // Prefer the server's guidance (Retry-After), fall back to backoff+jitter
          const retryAfterMs = parseRetryAfter(error.response?.headers?.["retry-after"]);
          const delay = retryAfterMs != null
            ? Math.min(retryAfterMs, 5000)       // cap at 5s so UX stays responsive
            : backoffDelay(config._retryCount);

          // iter 370ll — Was capping retry timeout at 6000ms which
          // killed slow endpoints like `/street-legal/invoice-preview`
          // (Webflow / Cloudflare invoices routinely take 10-15s to
          // download + Chromium-render on cold start). Bumped to
          // 20000ms so the retry inherits a generous-but-bounded
          // window. The original `config.timeout` is still respected
          // when smaller (so e.g. fast endpoints don't accidentally
          // stretch).
          config.timeout = Math.min(config.timeout || 10000, 20000);

          // Cancellable sleep so a `backend-down` event can abort pending retries
          const cancellable = new Promise((resolve, reject) => {
            const timer = setTimeout(resolve, delay);
            const cancel = () => { clearTimeout(timer); reject(new BackendDownError()); };
            pendingRetryCancellers.add(cancel);
            setTimeout(() => pendingRetryCancellers.delete(cancel), delay + 100);
          });
          try {
            await cancellable;
          } catch (e) {
            return Promise.reject(error); // backend-down abort — surface original error
          }
          return axios(config);
        }
      }
    }

    return Promise.reject(error);
  }
);

// Request interceptor — default timeouts + circuit breaker
axios.interceptors.request.use((config) => {
  if (!config.timeout) {
    const method = (config.method || "get").toLowerCase();
    // Mutations get 30s — ABOVE the server's 25s request budget — so the
    // client always receives the server's real verdict (success or 503)
    // instead of aborting at 20s while the write actually landed, which
    // made optimistic UI "revert" edits that had in fact saved.
    config.timeout = method === "get" ? 10000 : 30000;
  }

  // Circuit breaker: when backend is down, reject non-critical GETs
  // immediately so we don't pile more load on a struggling pod.
  if (isBackendDown() && !isHealthProbe(config) && !isMutation(config)) {
    const err = new BackendDownError();
    err.config = config;
    throw err;
  }

  return config;
});

// Adapter-level dedup: concurrent GETs to the same URL+params share one
// in-flight promise. Cuts the typical 20-endpoint dashboard burst down
// significantly when SWR and direct axios fire the same request in parallel.
// Uses axios.getAdapter() (axios 1.x) to resolve the active adapter so
// array-of-adapters configs are handled correctly.
const resolveActiveAdapter = () => {
  try {
    if (typeof axios.getAdapter === "function") {
      return axios.getAdapter(axios.defaults.adapter);
    }
  } catch { /* fall through */ }
  return axios.defaults.adapter;
};

const originalAdapter = resolveActiveAdapter();

/** Build a synthetic AxiosResponse from bootstrap-cache data. */
function synthesizeCacheResponse(config, data) {
  return {
    data,
    status: 200,
    statusText: "OK (bootstrap cache)",
    headers: { "x-bootstrap-cache": "hit" },
    config,
    request: null,
  };
}

axios.defaults.adapter = function dedupeAdapter(config) {
  if (!originalAdapter) return Promise.reject(new Error("No axios adapter available"));
  const method = (config.method || "get").toLowerCase();

  // ── Mutation? Invalidate bootstrap cache for that resource ────────
  // Keeps the UI consistent after writes. Since bootstrap keys map to
  // parent GETs (e.g. POST /api/customers invalidates GET /api/customers),
  // we match on URL prefix.
  if (method !== "get" && method !== "head") {
    try { invalidateBootstrapCache(config.url); } catch { /* noop */ }
    return originalAdapter(config);
  }

  if (config._skipDedup) return originalAdapter(config);

  // ── Bootstrap cache hit? Return synthetic response instantly ─────
  // This is what collapses 18 GETs into 1 — the bootstrap was already
  // fetched at login, so these hit cache and never touch the network.
  if (!config._skipBootstrapCache) {
    try {
      const cached = getCachedResponse(config.url);
      if (cached !== undefined) {
        return Promise.resolve(synthesizeCacheResponse(config, cached));
      }
    } catch { /* cache miss — fall through to network */ }
  }

  // ── In-flight dedup fallback ──────────────────────────────────────
  const key = inflightKey(config);
  const existing = inflightGets.get(key);
  if (existing) return existing;
  const promise = originalAdapter(config);
  inflightGets.set(key, promise);
  // .finally() derives a NEW promise that re-throws the rejection; without
  // the .catch() every failed GET produced an unhandled-rejection pageerror.
  promise.finally(() => {
    if (inflightGets.get(key) === promise) inflightGets.delete(key);
  }).catch(() => {});
  return promise;
};

export function resetChunkReloadCounter() { chunkReloadAttempts = 0; }

if (typeof window !== "undefined") {
  window.addEventListener("load", () => { chunkReloadAttempts = 0; });
}

export default axios;
